import { ApiError, request } from './http';
import { config } from './config';
import { getTariffName, getTariffOrder } from '../domain/tariffs';
import type { DiscountData, Rate, RouteAndRates, RoutePoint } from '../domain/types';

type RawRate = {
  rate: string;
  estimatedPrice?: number;
  price?: number;
  priceBeforeDiscount?: number;
  discount?: number;
  compensation?: number;
  compensationType?: number;
  highDemandRatio?: number;
  vehicleType?: string;
};

type RouteAndRatesEnvelope = {
  success?: boolean;
  error?: string | { code?: string; message?: string };
  data: {
    route: { geometry: string; distance: number; duration: number };
    rates: RawRate[];
    addresses?: Array<{ full?: string; short?: string }>;
    discountType?: number;
    compensationType?: number;
  };
};

// Reconstruct priceBeforeDiscount the way flutter_client RateModel.fromMap does:
// if the server sent `discount` but no `priceBeforeDiscount`, derive it as
// estimatedPrice + discount. Without this the struck-through price never shows
// even when a discount is actually present on the rate.
function buildRate(raw: RawRate, quoteCompensationType: number): Rate {
  // flutter_client RateModel.fromMap: `price` is the post-discount price
  // (estimatedPrice), NOT the raw `price` field — the rider displays and sends
  // this as priceEstimated. priceBeforeDiscount is reconstructed when absent.
  const estimated = raw.estimatedPrice ?? raw.price ?? 0;
  const discountAmount = raw.discount ?? 0;
  const rawBefore = raw.priceBeforeDiscount;
  const priceBeforeDiscount =
    rawBefore ??
    (discountAmount > 0 ? estimated + discountAmount : estimated);
  const serverBase = priceBeforeDiscount || estimated;

  return {
    rate: raw.rate,
    estimatedPrice: estimated,
    price: estimated,
    priceBeforeDiscount,
    originalBasePrice: serverBase,
    localPriceCalculate: estimated,
    discount: discountAmount,
    compensation: raw.compensation ?? 0,
    compensationType: raw.compensationType ?? quoteCompensationType ?? 0,
    highDemandRatio: raw.highDemandRatio,
    vehicleType: raw.vehicleType ?? getTariffName(raw.rate, config.defaultLanguage),
  };
}

// First discount value from the user-discount map (the rider app applies a single
// value across all rates — see _reconstructPriceBeforeDiscount in main_cubit_pricing).
function firstDiscountValue(discount: Record<string, number>): number {
  const values = Object.values(discount);
  return values.length > 0 ? values[0] : 0;
}

function maxDiscountAmount(discount: DiscountData): number {
  let max = 0;
  for (const v of Object.values(discount.maxValues)) {
    if (v > max) max = v;
  }
  return max;
}

// Mirrors flutter_client _reconstructPriceBeforeDiscount: rebuilds the pre-discount
// price from a user-level discount (fixed or percent), capped by maxDiscountAmount.
function reconstructBefore(
  finalPrice: number,
  discount: DiscountData,
  value: number,
  maxAmount: number,
): number | null {
  if (finalPrice <= 0 || value <= 0) return null;

  if (discount.discountType === 1) {
    return finalPrice + value;
  }

  if (discount.discountType === 2) {
    if (value <= 0 || value >= 100) return null;
    const baseFull = (finalPrice * 100) / (100 - value);
    const discountFull = baseFull - finalPrice;
    if (maxAmount > 0 && discountFull > maxAmount) {
      return finalPrice + maxAmount;
    }
    return baseFull;
  }

  return null;
}

// Apply a user-level discount to rates that don't already carry one. Mirrors the
// non-express branch of _applyDiscountToRates: keep server priceBeforeDiscount,
// else fall back to estimated + rate.discount, else reconstruct from the user
// discount endpoint. Returns new rates with priceBeforeDiscount/discount populated.
export function applyUserDiscountToRates(
  rates: Rate[],
  discount: DiscountData | null,
): Rate[] {
  if (!discount || discount.discountType === 0) return rates;
  const value = firstDiscountValue(discount.discount);
  if (value <= 0) return rates;
  const maxAmount = maxDiscountAmount(discount);

  return rates.map((rate) => {
    const estimated = rate.estimatedPrice;

    let before: number | null =
      rate.priceBeforeDiscount > estimated ? rate.priceBeforeDiscount : null;

    if (before == null && rate.discount > 0) {
      before = estimated + rate.discount;
    }

    if (before == null && estimated > 0) {
      const reconstructed = reconstructBefore(estimated, discount, value, maxAmount);
      if (reconstructed != null && reconstructed > estimated) {
        before = reconstructed;
      }
    }

    if (before == null || !(before > estimated)) {
      return rate;
    }

    const discountAmount = before - estimated;
    return {
      ...rate,
      priceBeforeDiscount: before,
      originalBasePrice: before,
      discount: discountAmount,
      compensation: discountAmount,
      compensationType: discountAmount > 0 ? rate.compensationType || 1 : 0,
    };
  });
}

// POST {cacheBaseUrl}/route-and-rates — single call returns the route geometry,
// distance/duration, and ALL tariff prices. This is the primary quote endpoint
// (flutter_client FeatureFlags.useOptimizedRouteEndpoint = true).
export async function getRouteAndRates(
  points: RoutePoint[],
  clientPhone: string,
): Promise<RouteAndRates> {
  const coordinates = points.map((p) => ({ lat: p.lat, lon: p.lon, address: p.address }));

  const res = await request<RouteAndRatesEnvelope>('/route-and-rates', {
    method: 'POST',
    baseUrl: config.cacheBaseUrl,
    auth: false,
    body: {
      coordinates,
      clientPhone,
      countryCode: config.defaultCountry,
      language: config.defaultLanguage,
    },
  });

  if (res.success === false || !res.data?.route || !Array.isArray(res.data.rates)) {
    const code = typeof res.error === 'string' ? res.error : res.error?.code;
    throw new ApiError(200, 'Route calculation failed', code ?? 'route_calculation_failed', res);
  }

  const d = res.data;
  const quoteCompensationType = d.compensationType;
  const rates = d.rates
    .map((r) => buildRate(r, quoteCompensationType ?? 0))
    .sort((a, b) => getTariffOrder(a.rate) - getTariffOrder(b.rate));

  return {
    route: d.route,
    rates,
    addresses: d.addresses,
    discountType: d.discountType,
    compensationType: d.compensationType,
  };
}
